import { useLayoutEffect, useMemo } from 'react';

import ConnectController from './ConnectController';
import { useConnectAuthCode } from './hooks/useConnectAuthCode';
import { useExchangeCache } from './hooks/useExchangeCache';

/**
 * /connect/resume — 로그인 / 채널 생성 후 복귀 진입점.
 *
 * 책임:
 *  - sessionStorage에서 authCode를 복구
 *  - 보관된 만료 시점이 지났으면 expired 상태로 전이 (useConnectAuthCode가 자동 처리)
 *  - 첫 exchange 응답이 캐시되어 있으면 그 결과를 그대로 ConnectController에 시드 주입
 *    → 동일 authCode 로 exchange API 를 재호출해 410(AuthCodeExpiredOrUsed) 이 발생하는 것을 회피
 *  - 캐시가 없거나 만료된 경우 ConnectController가 평소처럼 exchange를 재호출
 *  - 410/409 응답은 useExchangeAuthCode 에서 expired/error 로 폴백 (기존 처리 유지)
 *
 * URL에는 authCode가 노출되지 않으므로 ensureNoReferrerPolicy / scrubQueryString은 생략한다.
 * (다만 일관성을 위해 referrer 정책은 그대로 유지)
 */
export default function ConnectResumePage() {
  const { stored } = useConnectAuthCode();
  const { cached } = useExchangeCache();

  useLayoutEffect(() => {
    ensureNoReferrerPolicy();
  }, []);

  // stored가 null이면 ConnectController에서 자동으로 error 상태로 진입한다 (authCode === null).
  const authCode = useMemo(() => stored?.authCode ?? null, [stored]);

  // 캐시 hit 조건: authCode 일치 + 만료 전 (useExchangeCache.readStoredExchangeResult가 만료 처리).
  const cachedExchange = useMemo(
    () => (cached && authCode && cached.authCode === authCode ? cached : null),
    [cached, authCode]
  );

  return <ConnectController authCode={authCode} cachedExchange={cachedExchange} />;
}

function ensureNoReferrerPolicy() {
  const selector = 'meta[name="referrer"]';
  const existing = document.querySelector<HTMLMetaElement>(selector);
  if (existing) {
    existing.content = 'no-referrer';
    return;
  }

  const meta = document.createElement('meta');
  meta.name = 'referrer';
  meta.content = 'no-referrer';
  document.head.appendChild(meta);
}
